/**
 * Enriched Availability Hook
 * Combines explicit availability records with user's defaultAvailability
 */

import { useMemo } from 'react';
import { useAvailability } from '@/hooks/useAvailability';
import type { MealType, User } from '@/types/api';

// ==================== Types ====================

interface DateMealSlot {
  date: string;
  mealType: MealType;
}

interface EnrichedAvailabilityItem {
  date: string;
  mealType: MealType;
  isPresent: boolean;
  isExplicit: boolean; // true if from DB, false if from defaultAvailability
}

interface EnrichedAvailabilityData {
  availability: EnrichedAvailabilityItem[];
  total: number;
}

interface UseEnrichedAvailabilityParams {
  user: User;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  enabled?: boolean;
}

interface UseEnrichedAvailabilityResult {
  data: EnrichedAvailabilityData | undefined;
  isLoading: boolean;
  isError: boolean;
}

// ==================== Utility Functions ====================

/**
 * Generate all date/mealType combinations for a date range
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format (inclusive)
 * @returns Array of date-meal slot objects
 */
function generateDateMealSlots(
  startDate: string,
  endDate: string,
): DateMealSlot[] {
  const slots: DateMealSlot[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error('Invalid date format provided to generateDateMealSlots');
    return slots;
  }

  if (start > end) {
    console.error('Start date must be before or equal to end date');
    return slots;
  }

  // Generate all date+meal combinations
  const current = new Date(start);
  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0];

    // Add both lunch and dinner for each date
    slots.push({ date: dateStr, mealType: 'lunch' });
    slots.push({ date: dateStr, mealType: 'dinner' });

    // Move to next day
    current.setDate(current.getDate() + 1);
  }

  return slots;
}

/**
 * Create a lookup key for availability
 */
function createAvailabilityKey(date: string, mealType: MealType): string {
  return `${date}:${mealType}`;
}

// ==================== Main Hook ====================

/**
 * Hook to fetch enriched availability data for a user
 *
 * Combines explicit availability records from the API with the user's
 * defaultAvailability setting for dates without explicit records.
 *
 * Features:
 * - Generates complete date/meal grid for the range
 * - Fills gaps with user's defaultAvailability
 * - Marks each record as explicit or default
 * - Optimized with useMemo for performance
 *
 * @example
 * ```tsx
 * function AvailabilityCalendar() {
 *   const { user } = Route.useRouteContext();
 *   const { data, isLoading } = useEnrichedAvailability({
 *     user,
 *     startDate: '2025-01-01',
 *     endDate: '2025-01-07',
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data?.availability.map(avail => (
 *         <div key={`${avail.date}-${avail.mealType}`}>
 *           {avail.date} - {avail.mealType}:
 *           {avail.isPresent ? 'Present' : 'Absent'}
 *           {!avail.isExplicit && ' (default)'}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnrichedAvailability(
  params: UseEnrichedAvailabilityParams,
): UseEnrichedAvailabilityResult {
  const { user, startDate, endDate, enabled = true } = params;
  const userId = user.id;

  // Fetch explicit availability records from API
  // Note: useAvailability already has its own enabled logic based on startDate/endDate
  const {
    data: availabilityResponse,
    isLoading,
    isError,
  } = useAvailability({
    userId,
    startDate,
    endDate,
  });

  // Compute enriched availability data
  const enrichedData = useMemo<EnrichedAvailabilityData | undefined>(() => {
    // Return undefined while loading or if disabled
    if (!enabled || !availabilityResponse) {
      return undefined;
    }

    // Generate all date-meal slots for the range
    const allSlots = generateDateMealSlots(startDate, endDate);

    // Create a Map of explicit availability for O(1) lookup
    const explicitAvailabilityMap = new Map<string, boolean>();
    for (const record of availabilityResponse.availability) {
      const key = createAvailabilityKey(record.date, record.mealType);
      explicitAvailabilityMap.set(key, record.isPresent);
    }

    // Enrich each slot with availability data
    const enrichedAvailability: EnrichedAvailabilityItem[] = allSlots.map(
      (slot) => {
        const key = createAvailabilityKey(slot.date, slot.mealType);
        const explicitValue = explicitAvailabilityMap.get(key);

        // If explicit availability exists, use it
        if (explicitValue !== undefined) {
          return {
            date: slot.date,
            mealType: slot.mealType,
            isPresent: explicitValue,
            isExplicit: true,
          };
        }

        // Otherwise, use user's defaultAvailability
        return {
          date: slot.date,
          mealType: slot.mealType,
          isPresent: user.defaultAvailability,
          isExplicit: false,
        };
      },
    );

    return {
      availability: enrichedAvailability,
      total: enrichedAvailability.length,
    };
  }, [
    enabled,
    availabilityResponse,
    startDate,
    endDate,
    user.defaultAvailability,
  ]);

  return {
    data: enrichedData,
    isLoading,
    isError,
  };
}
